import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { mailAccountService } from '../../services/mail-account.service'
import { toastService } from '../../services/toast.service'
import { IMAPSecurity, MailAccount, MailAccountType } from '../../types'
import { parseBackendErrors } from '../../utils/errorHandler'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputText } from '../common/InputText'

interface MailAccountEditDialogProps {
  show: boolean
  onHide: () => void
  account?: MailAccount
  onSave: () => void
  readOnly?: boolean
}

export const MailAccountEditDialog: React.FC<MailAccountEditDialogProps> = ({ show, onHide, account, onSave, readOnly = false }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<MailAccount>>({
    name: '',
    imap_server: '',
    imap_port: 993,
    imap_security: IMAPSecurity.SSL,
    username: '',
    password: '',
    is_token: false,
    character_set: 'UTF-8',
    account_type: MailAccountType.IMAP,
    use_for_transactional_emails: false,
  })
  const [loading, setLoading] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<'success' | 'danger' | null>(null)
  const [testMessage, setTestMessage] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const imapSecurityOptions = [
    { value: IMAPSecurity.None, label: t('none') },
    { value: IMAPSecurity.SSL, label: 'SSL' },
    { value: IMAPSecurity.STARTTLS, label: 'STARTTLS' },
  ]

  // const accountTypeOptions = [
  //   { value: MailAccountType.IMAP, label: 'IMAP' },
  //   { value: MailAccountType.Gmail_OAuth, label: 'Gmail OAuth' },
  //   { value: MailAccountType.Outlook_OAuth, label: 'Outlook OAuth' },
  // ];

  useEffect(() => {
    if (account) {
      setFormData(account)
    } else {
      setFormData({
        name: '',
        imap_server: '',
        imap_port: 993,
        imap_security: IMAPSecurity.SSL,
        username: '',
        password: '',
        is_token: false,
        character_set: 'UTF-8',
        account_type: MailAccountType.IMAP,
        use_for_transactional_emails: false,
      })
    }
    setErrors({})
  }, [account, show])

  const handleInputChange = (field: keyof MailAccount, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleTest = async () => {
    setTestLoading(true)
    setTestResult(null)

    try {
      await mailAccountService.testMailAccount(formData as MailAccount)
      setTestResult('success')
      setTestMessage(t('connection_test_successful'))
    } catch (error: any) {
      setTestResult('danger')
      setTestMessage(error.response?.data?.detail || t('connection_test_failed'))
    } finally {
      setTestLoading(false)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      if (account) {
        await mailAccountService.updateMailAccount(account.id, formData)
        toastService.showSuccess(t('mail_account_updated', { name: formData.name }))
      } else {
        await mailAccountService.createMailAccount(formData)
        toastService.showSuccess(t('mail_account_created', { name: formData.name }))
      }
      onSave()
      onHide()
    } catch (error: any) {
      const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving_mail_account'))

      setErrors(fieldErrors)
      toastService.showError(t('error_saving_mail_account'), errorResponse)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    return account ? t('edit_mail_account', { name: account.name }) : t('create_mail_account')
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {getTitle()}
          {account && <span className="badge bg-primary ms-2">ID: {account.id}</span>}
        </Modal.Title>
      </Modal.Header>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (readOnly) return
          handleSave()
        }}
      >
        <Modal.Body>
          <fieldset
            disabled={readOnly}
            aria-disabled={readOnly}
            className="border-0 p-0 m-0"
            style={readOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
          >
          {errors._general && (
            <Alert variant="danger" className="mb-3">
              {errors._general}
            </Alert>
          )}
          <Row>
            <Col>
              <InputText title={t('name')} value={formData.name || ''} onChange={(value) => handleInputChange('name', value)} error={errors.name} />

              <InputText
                title={t('imap_server')}
                value={formData.imap_server || ''}
                onChange={(value) => handleInputChange('imap_server', value)}
                error={errors.imap_server}
              />

              <InputText
                title={t('imap_port')}
                type="number"
                value={formData.imap_port || 993}
                onChange={(value) => handleInputChange('imap_port', parseInt(value) || 993)}
                error={errors.imap_port}
              />

              <InputSelect
                title={t('imap_security')}
                value={formData.imap_security || IMAPSecurity.SSL}
                onChange={(value) => handleInputChange('imap_security', value)}
                items={imapSecurityOptions.map((option) => ({ id: option.value, name: option.label }))}
                bindLabel="name"
              />
            </Col>

            <Col>
              <InputText
                title={t('username')}
                value={formData.username || ''}
                onChange={(value) => handleInputChange('username', value)}
                error={errors.username}
              />

              <InputText
                title={t('password')}
                type="password"
                value={formData.password || ''}
                onChange={(value) => handleInputChange('password', value)}
                error={errors.password}
              />

              <InputSwitch
                title={t('password_is_token')}
                value={formData.is_token || false}
                onChange={(value) => handleInputChange('is_token', value)}
                horizontal={true}
                hint={t('password_is_token_hint')}
              />

              <InputText
                title={t('character_set')}
                value={formData.character_set || 'UTF-8'}
                onChange={(value) => handleInputChange('character_set', value)}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <InputSwitch
                title={t('use_for_transactional_emails')}
                value={formData.use_for_transactional_emails || false}
                onChange={(value) => handleInputChange('use_for_transactional_emails', value)}
                horizontal={true}
                hint={t('use_for_transactional_emails_hint')}
              />
            </Col>
          </Row>

          {formData.use_for_transactional_emails &&
            formData.account_type !== MailAccountType.Gmail_OAuth &&
            formData.account_type !== MailAccountType.Outlook_OAuth && (
              <Row className="mt-3">
                <Col>
                  <h6 className="mb-3">{t('smtp_settings')}</h6>
                </Col>
              </Row>
            )}

          {formData.use_for_transactional_emails &&
            formData.account_type !== MailAccountType.Gmail_OAuth &&
            formData.account_type !== MailAccountType.Outlook_OAuth && (
              <Row>
                <Col>
                  <InputText
                    title={t('smtp_server')}
                    value={formData.smtp_server || ''}
                    onChange={(value) => handleInputChange('smtp_server', value)}
                    error={errors.smtp_server}
                    hint={t('smtp_server_hint')}
                  />
                </Col>
                <Col>
                  <InputText
                    title={t('smtp_port')}
                    type="number"
                    value={formData.smtp_port || ''}
                    onChange={(value) => handleInputChange('smtp_port', value ? parseInt(value) : undefined)}
                    error={errors.smtp_port}
                    hint={t('smtp_port_hint')}
                  />
                </Col>
                <Col>
                  <InputSelect
                    title={t('smtp_security')}
                    value={formData.smtp_security || formData.imap_security || IMAPSecurity.SSL}
                    onChange={(value) => handleInputChange('smtp_security', value)}
                    items={imapSecurityOptions.map((option) => ({ id: option.value, name: option.label }))}
                    bindLabel="name"
                  />
                </Col>
              </Row>
            )}
          </fieldset>
        </Modal.Body>

        <Modal.Footer>
          {testResult && (
            <Alert variant={testResult} className="mb-0 me-2">
              {testMessage}
            </Alert>
          )}

          <Button
            type="button"
            variant="outline-primary"
            onClick={handleTest}
            disabled={loading || testLoading || readOnly}
            title={readOnly ? t('you_dont_have_permissions_to_do_that') : undefined}
          >
            {testLoading && (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">{t('loading')}...</span>
              </div>
            )}
            {t('test')}
          </Button>

          <Button type="button" variant="outline-secondary" onClick={onHide} disabled={loading}>
            {t('cancel')}
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading || readOnly}
            title={readOnly ? t('you_dont_have_permissions_to_do_that') : undefined}
          >
            {loading && (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">{t('loading')}...</span>
              </div>
            )}
            {t('save')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
